import { motion } from "framer-motion";
import { Bell, AtSign, UserPlus, CalendarCheck, AlertTriangle, ScrollText, ShieldCheck, Lock, Check, FileText, Printer, Sparkles, ListChecks, Building2, ClipboardCheck, RotateCcw, Eye, Megaphone, CalendarClock, Layers, Undo2, CheckSquare, Repeat, BellRing, Users } from "lucide-react";
import { STATUS_COLORS } from "./DashboardMockup";

const EASE = [0.22, 1, 0.36, 1];

const reveal = {
  initial: { opacity: 0, y: 48 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.9, ease: EASE },
};

const LANES = Object.entries(STATUS_COLORS);

const DONUT_BG = (() => {
  const colors = Object.values(STATUS_COLORS);
  const step = 360 / colors.length;
  return `conic-gradient(${colors.map((c, i) => `${c} ${i * step}deg ${(i + 1) * step}deg`).join(", ")})`;
})();

const VisualCard = ({ children, className = "" }) => (
  <div className={`relative overflow-hidden rounded-3xl border border-white/10 bg-[#0A120D]/70 p-7 backdrop-blur-md ${className}`}>
    <div className="pointer-events-none absolute -inset-16 rounded-full bg-[#00FF66]/8 blur-[90px]" aria-hidden="true" />
    <div className="relative">{children}</div>
  </div>
);

const ChapterNumber = ({ num }) => (
  <span className="font-display text-outline shrink-0 text-5xl font-black leading-none md:text-6xl">{num}</span>
);

/* ---------- status donut ---------- */
const StatusDonut = () => (
  <VisualCard>
    <p className="mb-5 text-[10px] font-bold uppercase tracking-[0.24em] text-white/40">Status breakdown</p>
    <div className="flex items-center gap-7">
      <motion.div
        initial={{ opacity: 0, scale: 0.7, rotate: -50 }}
        whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 1, ease: EASE }}
        className="relative h-36 w-36 shrink-0"
        style={{ background: DONUT_BG, borderRadius: "9999px" }}
      >
        <div className="absolute inset-[24%] flex flex-col items-center justify-center rounded-full bg-[#0A120D]">
          <span className="font-display text-2xl font-black text-white">6</span>
          <span className="text-[8px] font-bold uppercase tracking-[0.2em] text-white/40">lanes</span>
        </div>
      </motion.div>
      <div className="space-y-1.5">
        {LANES.map(([label, color], i) => (
          <motion.div
            key={label}
            initial={{ opacity: 0, x: 12 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5, delay: 0.3 + i * 0.08, ease: EASE }}
            className="flex items-center gap-2"
          >
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: color }} />
            <span className="text-[11px] font-medium text-white/60">{label}</span>
          </motion.div>
        ))}
      </div>
    </div>
  </VisualCard>
);

/* ---------- kanban board ---------- */
const BOARD_LANES = ["NOT STARTED", "IN PROGRESS", "REVIEW", "DONE"];
const BOARD_CARDS = { "NOT STARTED": 2, "IN PROGRESS": 3, REVIEW: 1, DONE: 2 };

const BoardViz = () => (
  <VisualCard>
    <div className="mb-5 flex items-center justify-between">
      <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-white/40">Task board</p>
      <span className="rounded-full border border-[#00FF66]/40 bg-[#00FF66]/10 px-2.5 py-0.5 text-[9px] font-bold text-[#00FF66]">8 tasks</span>
    </div>
    <div className="flex gap-2.5">
      {BOARD_LANES.map((label, col) => {
        const color = STATUS_COLORS[label];
        return (
          <div key={label} className="flex-1 rounded-2xl border border-white/5 bg-white/[0.02] p-2">
            <div className="mb-2 flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: color }} />
              <span className="truncate text-[7px] font-bold uppercase tracking-widest text-white/50">{label}</span>
            </div>
            {Array.from({ length: BOARD_CARDS[label] }).map((_, k) => (
              <motion.div
                key={k}
                initial={{ opacity: 0, y: 10, scale: 0.96 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: 0.15 + col * 0.12 + k * 0.08, ease: EASE }}
                className="mb-1.5 rounded-lg border border-white/5 bg-white/[0.05] p-1.5"
              >
                <div className="mb-1 h-1 w-3/4 rounded-full bg-white/20" />
                <div className="flex items-center justify-between">
                  <span className="h-0.5 w-6 rounded-full bg-white/10" />
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: color }} />
                </div>
              </motion.div>
            ))}
          </div>
        );
      })}
    </div>
  </VisualCard>
);

/* ---------- analytics (donut + momentum) ---------- */
const AnalyticsViz = () => (
  <VisualCard>
    <div className="mb-5 flex items-center justify-between">
      <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-white/40">Momentum</p>
      <span className="rounded-full bg-[#00FF66]/12 px-2.5 py-0.5 text-[9px] font-bold text-[#00FF66]">+18% this week</span>
    </div>
    <div className="flex items-center gap-7">
      <motion.div
        initial={{ opacity: 0, scale: 0.7 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.9, ease: EASE }}
        className="relative h-24 w-24 shrink-0"
        style={{ background: DONUT_BG, borderRadius: "9999px" }}
      >
        <div className="absolute inset-[24%] flex items-center justify-center rounded-full bg-[#0A120D]">
          <span className="font-display text-lg font-black text-white">40%</span>
        </div>
      </motion.div>
      <svg viewBox="0 0 120 48" className="h-20 flex-1" preserveAspectRatio="none">
        <motion.path
          d="M0,42 L20,34 L40,36 L60,22 L80,24 L100,12 L120,6"
          fill="none"
          stroke="#00FF66"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={{ pathLength: 0, opacity: 0 }}
          whileInView={{ pathLength: 1, opacity: 1 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 1.4, ease: EASE, delay: 0.2 }}
        />
      </svg>
    </div>
  </VisualCard>
);

/* ---------- workload bars ---------- */
const OWNERS = [
  { name: "User 1", load: 78, color: "#90C2E7" },
  { name: "User 2", load: 96, color: "#FF8B94", warn: true },
  { name: "User 3", load: 44, color: "#A8E6CF" },
  { name: "User 4", load: 18, color: "#FFD3B6", pto: true },
  { name: "User 5", load: 61, color: "#FFC3A0" },
];

const WorkloadBars = () => (
  <VisualCard>
    <div className="mb-5 flex items-center justify-between">
      <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-white/40">Assignment summary</p>
      <span className="text-[10px] font-semibold text-white/30">this week</span>
    </div>
    <div className="space-y-3.5">
      {OWNERS.map((o, i) => (
        <div key={o.name} className="flex items-center gap-3">
          <span className="w-20 shrink-0 text-[11px] font-semibold text-white/70">{o.name}</span>
          <div className="relative h-2 flex-1 overflow-hidden rounded-full bg-white/[0.06]">
            <motion.div
              className="h-full rounded-full"
              style={{ backgroundColor: o.color }}
              initial={{ width: 0 }}
              whileInView={{ width: `${o.load}%` }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 1.1, delay: 0.2 + i * 0.1, ease: EASE }}
            />
          </div>
          {o.warn ? (
            <span className="flex shrink-0 items-center gap-1 rounded-full bg-[#FF8B94]/15 px-2 py-0.5 text-[9px] font-bold text-[#FF8B94]">
              <AlertTriangle className="h-2.5 w-2.5" /> Overloaded
            </span>
          ) : o.pto ? (
            <span className="shrink-0 rounded-full bg-[#FFD3B6]/15 px-2 py-0.5 text-[9px] font-bold text-[#FFD3B6]">On PTO</span>
          ) : (
            <span className="w-9 shrink-0 text-right text-[10px] font-semibold text-white/35">{o.load}%</span>
          )}
        </div>
      ))}
    </div>
  </VisualCard>
);

/* ---------- PTO calendar ---------- */
const CAL_HIGHLIGHTS = { 3: "#FFD3B6", 11: "#00FF66", 12: "#00FF66", 17: "#90C2E7", 18: "#90C2E7", 24: "#A8E6CF" };

const CalendarViz = () => (
  <VisualCard>
    <div className="mb-4 flex items-center justify-between">
      <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-white/40">July</p>
      <span className="rounded-full bg-[#FFD3B6]/20 px-2 py-0.5 text-[9px] font-bold text-[#FFD3B6]">Holiday · Jul 4</span>
    </div>
    <div className="mb-1.5 grid grid-cols-7 gap-1.5">
      {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
        <span key={i} className="text-center text-[8px] font-bold uppercase text-white/25">{d}</span>
      ))}
    </div>
    <div className="grid grid-cols-7 gap-1.5">
      {Array.from({ length: 28 }).map((_, i) => {
        const color = CAL_HIGHLIGHTS[i];
        return (
          <motion.div
            key={i}
            initial={color ? { scale: 0.4, opacity: 0 } : false}
            whileInView={color ? { scale: 1, opacity: 1 } : {}}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.4, delay: 0.2 + (i % 7) * 0.03, ease: EASE }}
            className="flex aspect-square items-center justify-center rounded-md text-[9px] font-semibold"
            style={{
              backgroundColor: color || "rgba(255,255,255,0.04)",
              color: color ? "#050505" : "rgba(255,255,255,0.3)",
            }}
          >
            {i + 1}
          </motion.div>
        );
      })}
    </div>
    <div className="mt-4 flex flex-wrap gap-3 text-[9px] font-semibold text-white/40">
      <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[#00FF66]" /> Vacation</span>
      <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[#90C2E7]" /> Sick</span>
      <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[#FFD3B6]" /> Holiday</span>
    </div>
  </VisualCard>
);

/* ---------- collaboration / notifications ---------- */
const SIGNALS = [
  { icon: UserPlus, tag: "Assignment", text: "You were assigned TASK #4", color: "#90C2E7" },
  { icon: AtSign, tag: "Mention", text: "@you in TASK #2 discussion", color: "#00FF66" },
  { icon: CalendarCheck, tag: "Time off", text: "Your PTO request was approved", color: "#A8E6CF" },
];

const SignalStack = () => (
  <VisualCard>
    <div className="mb-5 flex items-center justify-between">
      <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-white/40">Notifications</p>
      <span className="relative inline-flex">
        <Bell className="h-4 w-4 text-white/60" />
        <span className="absolute -right-1.5 -top-1.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-[#00FF66] text-[7px] font-black text-black">3</span>
      </span>
    </div>
    <div className="space-y-2.5">
      {SIGNALS.map((s, i) => (
        <motion.div
          key={s.text}
          initial={{ opacity: 0, x: 24 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6, delay: 0.15 + i * 0.14, ease: EASE }}
          className="flex items-center gap-3 rounded-2xl border border-white/5 bg-white/[0.03] px-3.5 py-3"
        >
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg" style={{ backgroundColor: `${s.color}22` }}>
            <s.icon className="h-3.5 w-3.5" style={{ color: s.color }} />
          </span>
          <div className="min-w-0">
            <p className="truncate text-[11px] font-semibold text-white/75">{s.text}</p>
            <p className="text-[9px] font-bold uppercase tracking-widest" style={{ color: s.color }}>{s.tag}</p>
          </div>
        </motion.div>
      ))}
    </div>
  </VisualCard>
);

/* ---------- audit log ---------- */
const AUDIT_ROWS = [
  { actor: "User 1", action: "updated status", target: "TASK #1", time: "2m", color: "#90C2E7" },
  { actor: "User 2", action: "commented on", target: "TASK #2", time: "14m", color: "#D8B4E2" },
  { actor: "Admin", action: "approved time off", target: "User 3", time: "1h", color: "#00FF66" },
  { actor: "Admin", action: "exported audit CSV", target: "—", time: "3h", color: "#FFC3A0" },
  { actor: "User 5", action: "moved to review", target: "TASK #6", time: "5h", color: "#FFD3B6" },
];

const AuditViz = () => (
  <VisualCard>
    <p className="mb-5 flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.24em] text-[#00FF66]">
      <ScrollText className="h-3.5 w-3.5" /> Audit log
    </p>
    <div className="overflow-hidden rounded-2xl border border-white/5">
      {AUDIT_ROWS.map((row, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, x: -16 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.45, delay: 0.1 + i * 0.09, ease: EASE }}
          className="flex items-center gap-2.5 border-b border-white/5 px-3.5 py-2.5 font-mono text-[10px] last:border-0"
        >
          <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: row.color }} />
          <span className="w-20 shrink-0 truncate font-semibold text-white/80">{row.actor}</span>
          <span className="text-white/45">{row.action}</span>
          <span className="truncate text-white/70">{row.target}</span>
          <span className="ml-auto shrink-0 text-white/30">{row.time} ago</span>
        </motion.div>
      ))}
    </div>
  </VisualCard>
);

/* ---------- RBAC / access ---------- */
const RbacViz = () => (
  <VisualCard>
    <p className="mb-5 flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.24em] text-[#00FF66]">
      <Lock className="h-3.5 w-3.5" /> Roles &amp; access
    </p>
    <div className="grid grid-cols-2 gap-4">
      <div className="rounded-2xl border border-[#00FF66]/25 bg-[#00FF66]/[0.05] p-4">
        <p className="mb-3 flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-[#00FF66]">
          <ShieldCheck className="h-3.5 w-3.5" /> Admin
        </p>
        <ul className="space-y-2 text-[11px] text-white/60">
          <li className="flex gap-1.5"><Check className="h-3 w-3 shrink-0 text-[#00FF66]" /> Manage projects &amp; team</li>
          <li className="flex gap-1.5"><Check className="h-3 w-3 shrink-0 text-[#00FF66]" /> Approve time off</li>
          <li className="flex gap-1.5"><Check className="h-3 w-3 shrink-0 text-[#00FF66]" /> View audit log</li>
        </ul>
      </div>
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
        <p className="mb-3 text-xs font-bold uppercase tracking-widest text-white/50">Member</p>
        <ul className="space-y-2 text-[11px] text-white/60">
          <li className="flex gap-1.5"><Check className="h-3 w-3 shrink-0 text-white/40" /> Own tasks only</li>
          <li className="flex gap-1.5"><Check className="h-3 w-3 shrink-0 text-white/40" /> Request time off</li>
          <li className="flex gap-1.5"><Lock className="h-3 w-3 shrink-0 text-white/40" /> Ownership-locked edits</li>
        </ul>
      </div>
    </div>
    <div className="mt-4 flex flex-wrap gap-2">
      {[
        { label: "Project lead", color: "#00FF66" },
        { label: "Group lead", color: "#90C2E7" },
        { label: "Reviewer", color: "#D8B4E2" },
        { label: "Viewer · read-only", color: "#FFD3B6", icon: true },
      ].map((r) => (
        <span key={r.label} className="flex items-center gap-1 rounded-full border px-2.5 py-1 text-[9px] font-bold" style={{ borderColor: `${r.color}55`, color: r.color }}>
          {r.icon && <Eye className="h-2.5 w-2.5" />} {r.label}
        </span>
      ))}
    </div>
  </VisualCard>
);

/* ---------- printable report (mirrors the real report layout) ---------- */
const REPORT_STATS = [
  { label: "Total tasks", value: "5", color: "#0b0b0b" },
  { label: "Done", value: "1", color: "#0f9d58" },
  { label: "In progress", value: "1", color: "#1a73e8" },
  { label: "Overdue", value: "1", color: "#d93025" },
  { label: "Avg progress", value: "68%", color: "#0b0b0b" },
];
const REPORT_STATUS = [
  ["NOT STARTED", 1], ["IN PROGRESS", 1], ["DONE", 1], ["ON HOLD", 0], ["DEPRIORITIZED", 0], ["REVIEW", 2],
];

const ReportViz = () => (
  <VisualCard>
    <div className="mb-4 flex items-center justify-between">
      <p className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.24em] text-[#00FF66]">
        <FileText className="h-3.5 w-3.5" /> Printable report
      </p>
      <span className="flex items-center gap-1 rounded-full bg-[#00FF66]/12 px-2.5 py-0.5 text-[9px] font-bold text-[#00FF66]">
        <Printer className="h-2.5 w-2.5" /> Save as PDF
      </span>
    </div>
    <motion.div
      initial={{ opacity: 0, y: 16, scale: 0.96 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, ease: EASE }}
      className="rounded-xl bg-white p-4 text-[#0b0b0b] shadow-[0_20px_50px_rgba(0,0,0,0.4)]"
    >
      {/* header */}
      <div className="flex items-start justify-between border-b border-black/10 pb-2.5">
        <div className="flex items-center gap-1.5">
          <span className="h-3 w-3 rotate-45 rounded-[2px] bg-[#00C24E]" />
          <div>
            <p className="font-display text-[12px] font-black leading-none tracking-[0.18em]">VEKTOR</p>
            <p className="mt-0.5 text-[6px] font-bold uppercase tracking-widest text-black/40">Project status report</p>
          </div>
        </div>
        <div className="text-right text-[6.5px] leading-relaxed text-black/45">
          <p><span className="font-bold text-black/65">Project:</span> All projects</p>
          <p><span className="font-bold text-black/65">Generated:</span> Aug 26, 2026</p>
          <p><span className="font-bold text-black/65">Prepared by:</span> Admin</p>
        </div>
      </div>

      {/* stat cards */}
      <div className="mt-3 grid grid-cols-5 gap-1.5">
        {REPORT_STATS.map((s) => (
          <div key={s.label} className="rounded-md border border-black/10 bg-black/[0.02] p-1.5">
            <p className="text-[5.5px] font-bold uppercase leading-tight tracking-widest text-black/40">{s.label}</p>
            <p className="font-display mt-0.5 text-sm font-black leading-none" style={{ color: s.color }}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* AI executive summary */}
      <div className="mt-3 rounded-md border border-[#00C24E]/30 bg-[#00C24E]/[0.07] p-2">
        <p className="flex items-center gap-1 text-[6px] font-bold uppercase tracking-widest text-[#0f9d58]">
          <Sparkles className="h-2 w-2" /> Executive summary · AI
        </p>
        <div className="mt-1 space-y-1">
          <div className="h-1 w-full rounded-full bg-black/[0.12]" />
          <div className="h-1 w-11/12 rounded-full bg-black/[0.12]" />
          <div className="h-1 w-2/3 rounded-full bg-black/[0.12]" />
        </div>
      </div>

      {/* status breakdown */}
      <p className="mt-3 text-[6px] font-bold uppercase tracking-widest text-black/40">Status breakdown</p>
      <div className="mt-1.5 space-y-1">
        {REPORT_STATUS.map(([label, count]) => (
          <div key={label} className="flex items-center gap-2">
            <span className="w-16 shrink-0 text-[6.5px] font-semibold text-black/60">{label}</span>
            <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-black/[0.06]">
              <div className="h-full rounded-full" style={{ width: `${(count / 5) * 100}%`, backgroundColor: STATUS_COLORS[label] }} />
            </div>
            <span className="w-3 shrink-0 text-right text-[6.5px] font-semibold text-black/50">{count}</span>
          </div>
        ))}
      </div>
    </motion.div>
  </VisualCard>
);

/* ---------- timeline / gantt ---------- */
const TL_BARS = [
  { left: 6, width: 30, key: "IN PROGRESS", progress: 55 },
  { left: 20, width: 44, key: "REVIEW", progress: 35 },
  { left: 42, width: 26, key: "NOT STARTED", overdue: true },
  { left: 58, width: 32, key: "DONE", progress: 100 },
];
const TimelineViz = () => (
  <VisualCard>
    <div className="mb-4 flex items-center justify-between">
      <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-white/40">Timeline</p>
      <span className="rounded-full border border-[#00FF66]/40 bg-[#00FF66]/10 px-2.5 py-0.5 text-[9px] font-bold text-[#00FF66]">Day view</span>
    </div>
    <div className="mb-2 flex justify-between px-1 text-[8px] font-semibold tabular-nums text-white/25">
      {[24, 25, 26, 27, 28, 29, 30, 31].map((d) => (
        <span key={d}>{d}</span>
      ))}
    </div>
    <div className="relative space-y-3">
      <div className="absolute bottom-0 top-0 z-10 w-px bg-[#00FF66]/70" style={{ left: "46%" }}>
        <span className="absolute -top-2 left-1 rounded-sm bg-[#00FF66] px-1 text-[7px] font-black uppercase text-black">Today</span>
      </div>
      <svg className="pointer-events-none absolute inset-0 z-20" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
        <path d="M 64 40 C 72 40, 72 62, 42 62" fill="none" stroke="#FF8B94" strokeWidth="0.5" strokeOpacity="0.9" vectorEffect="non-scaling-stroke" strokeDasharray="4 3" />
      </svg>
      {TL_BARS.map((b, i) => {
        const color = STATUS_COLORS[b.key] || "#90C2E7";
        return (
          <div key={i} className="relative h-4">
            <motion.div
              initial={{ scaleX: 0, opacity: 0 }}
              whileInView={{ scaleX: 1, opacity: 1 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: 0.15 + i * 0.12, ease: EASE }}
              className="absolute top-0 h-4 origin-left overflow-hidden rounded-md"
              style={{
                left: `${b.left}%`,
                width: `${b.width}%`,
                backgroundColor: `${color}33`,
                boxShadow: b.overdue ? "inset 0 0 0 1.5px #FF8B94" : `inset 0 0 0 1px ${color}`,
              }}
            >
              {b.progress != null && <span className="absolute inset-y-0 left-0 rounded-md" style={{ width: `${b.progress}%`, backgroundColor: color }} />}
            </motion.div>
          </div>
        );
      })}
    </div>
  </VisualCard>
);

/* ---------- mind map ---------- */
const MM_PALETTE = ["#FF8B94", "#FFD3B6", "#A8E6CF", "#90C2E7", "#D8B4E2"];
const MM_NOTES = [
  { x: 3, y: 4, w: 40, color: "#FF8B94" },
  { x: 54, y: 30, w: 42, color: "#90C2E7" },
  { x: 16, y: 60, w: 40, color: "#A8E6CF" },
];
const MindmapViz = () => {
  return (
    <VisualCard>
      <div className="mb-4 flex items-center justify-between">
        <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-white/40">Mind map</p>
        <span className="rounded-full border border-[#00FF66]/40 bg-[#00FF66]/10 px-2.5 py-0.5 text-[9px] font-bold text-[#00FF66]">Private</span>
      </div>
      <div className="relative h-52">
        {MM_NOTES.map((n, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, scale: 0.8, y: 12 }}
            whileInView={{ opacity: 1, scale: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5, delay: 0.15 + i * 0.12, ease: EASE }}
            className="absolute z-10 rounded-lg p-2 shadow-[0_8px_24px_rgba(0,0,0,0.45)]"
            style={{ left: `${n.x}%`, top: `${n.y}%`, width: `${n.w}%`, backgroundColor: n.color }}
          >
            <div className="mb-1.5 h-1 w-1/2 rounded-full bg-black/30" />
            <div className="mb-1 h-1 w-3/4 rounded-full bg-black/15" />
            <div className="mb-2 h-1 w-2/3 rounded-full bg-black/15" />
            <div className="flex gap-1">
              {MM_PALETTE.map((c) => (
                <span key={c} className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: c, boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.15)" }} />
              ))}
            </div>
          </motion.div>
        ))}
      </div>
    </VisualCard>
  );
};


/* ---------- my work (cross-workspace personal hub) ---------- */
const MYWORK_GROUPS = [
  { label: "Overdue", accent: "#FF8B94", rows: [
    { title: "Isometric drawings LGN", ws: "Northwind Energy", due: "3d overdue" },
  ] },
  { label: "Due soon", accent: "#FFB020", rows: [
    { title: "Order gaskets", ws: "Northwind Energy", due: "in 2d" },
  ] },
  { label: "Later", accent: "#A8E6CF", rows: [
    { title: "Schedule crane lift", ws: "Harbor Works", due: "in 12d" },
  ] },
];

const MyWorkViz = () => (
  <VisualCard>
    <div className="mb-4 flex items-center justify-between">
      <p className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.24em] text-[#00FF66]">
        <ListChecks className="h-3.5 w-3.5" /> My work
      </p>
      <span className="rounded-full bg-[#FF8B94]/15 px-2.5 py-0.5 text-[9px] font-bold text-[#FF8B94]">1 overdue · 2 workspaces</span>
    </div>
    <div className="space-y-4">
      {MYWORK_GROUPS.map((g, gi) => (
        <div key={g.label}>
          <p className="mb-1.5 text-[8px] font-bold uppercase tracking-widest" style={{ color: g.accent }}>{g.label}</p>
          <div className="space-y-1.5">
            {g.rows.map((r, i) => (
              <motion.div
                key={r.title}
                initial={{ opacity: 0, x: -12 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.45, delay: 0.1 + gi * 0.12 + i * 0.08, ease: EASE }}
                className="flex items-center gap-2.5 rounded-xl border px-3 py-2"
                style={{ borderColor: g.label === "Overdue" ? "rgba(255,139,148,0.3)" : "rgba(255,255,255,0.06)", backgroundColor: g.label === "Overdue" ? "rgba(255,139,148,0.05)" : "rgba(255,255,255,0.02)" }}
              >
                <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: g.accent }} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[11px] font-semibold text-white/80">{r.title}</p>
                  <p className="flex items-center gap-1 text-[8px] font-bold uppercase tracking-widest text-white/40">
                    <Building2 className="h-2.5 w-2.5 text-[#00FF66]/60" /> {r.ws}
                  </p>
                </div>
                <span className="shrink-0 rounded-full px-2 py-0.5 text-[8px] font-bold" style={{ backgroundColor: `${g.accent}22`, color: g.accent }}>{r.due}</span>
              </motion.div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </VisualCard>
);


/* ---------- review workflow ---------- */
const REVIEW_ROWS = [
  { title: "P&ID markups — Unit 200", who: "Reviewer · User 2", age: "1d in review", cycles: 1, color: "#00FF66" },
  { title: "Pipe support calcs", who: "Reviewer · Lead", age: "4d in review", cycles: 2, color: "#FFB020", late: true },
];

const ReviewViz = () => (
  <VisualCard>
    <div className="mb-5 flex items-center justify-between">
      <p className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.24em] text-[#00FF66]">
        <ClipboardCheck className="h-3.5 w-3.5" /> To review
      </p>
      <span className="rounded-full bg-[#FF8B94] px-2 py-0.5 text-[9px] font-black text-black">2</span>
    </div>
    <div className="space-y-2.5">
      {REVIEW_ROWS.map((r, i) => (
        <motion.div
          key={r.title}
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.5, delay: 0.15 + i * 0.14, ease: EASE }}
          className="rounded-2xl border border-white/5 bg-white/[0.03] p-3.5"
        >
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: STATUS_COLORS.REVIEW }} />
            <p className="min-w-0 flex-1 truncate text-[11px] font-semibold text-white/80">{r.title}</p>
            <span className="shrink-0 rounded-full border border-white/10 px-1.5 py-0.5 text-[8px] font-bold text-white/50">{r.cycles} cycle{r.cycles > 1 ? "s" : ""}</span>
          </div>
          <div className="mt-2 flex items-center gap-2 text-[8px] font-bold uppercase tracking-widest">
            <span className="text-white/35">{r.who}</span>
            <span style={{ color: r.color }}>· {r.age}</span>
            <span className="ml-auto flex gap-1.5 normal-case tracking-normal">
              <span className="flex items-center gap-1 rounded-full bg-[#00FF66] px-2 py-0.5 text-[8px] font-black text-black"><Check className="h-2.5 w-2.5" /> Approve</span>
              <span className="flex items-center gap-1 rounded-full border border-[#FFB020]/40 px-2 py-0.5 text-[8px] font-bold text-[#FFB020]"><RotateCcw className="h-2.5 w-2.5" /> Changes</span>
            </span>
          </div>
        </motion.div>
      ))}
    </div>
  </VisualCard>
);

/* ---------- discipline groups ---------- */
const GROUPS = [
  { name: "Mechanical", lead: "User 1", members: 4, color: "#90C2E7" },
  { name: "Piping", lead: "User 3", members: 5, color: "#A8E6CF" },
  { name: "Electrical", lead: "User 5", members: 3, color: "#FFD3B6" },
];

const GroupsViz = () => (
  <VisualCard>
    <div className="mb-5 flex items-center justify-between">
      <p className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.24em] text-white/40">
        <Layers className="h-3.5 w-3.5" /> Project · Plant expansion
      </p>
      <span className="rounded-full border border-[#00FF66]/40 bg-[#00FF66]/10 px-2.5 py-0.5 text-[9px] font-bold text-[#00FF66]">3 disciplines</span>
    </div>
    <div className="space-y-2.5">
      {GROUPS.map((g, i) => (
        <motion.div
          key={g.name}
          initial={{ opacity: 0, x: 16 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.5, delay: 0.12 + i * 0.12, ease: EASE }}
          className="flex items-center gap-3 rounded-2xl border border-white/5 bg-white/[0.03] px-3.5 py-3"
        >
          <span className="rounded-md px-2 py-0.5 text-[9px] font-black uppercase tracking-widest text-black" style={{ backgroundColor: g.color }}>{g.name}</span>
          <span className="text-[10px] font-semibold text-white/55">Lead · {g.lead}</span>
          <div className="ml-auto flex -space-x-1.5">
            {Array.from({ length: g.members }).map((_, k) => (
              <span key={k} className="h-4 w-4 rounded-full border border-[#0A120D] bg-white/20" />
            ))}
          </div>
        </motion.div>
      ))}
    </div>
  </VisualCard>
);

/* ---------- bulk edit + recycle bin ---------- */
const BulkViz = () => (
  <VisualCard>
    <div className="mb-4 flex items-center justify-between">
      <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-white/40">Tasks</p>
      <span className="rounded-full bg-[#FFB020]/15 px-2.5 py-0.5 text-[9px] font-bold text-[#FFB020]">Recently deleted · 2</span>
    </div>
    <div className="space-y-1.5">
      {[true, true, false, true].map((sel, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, x: -12 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.4, delay: 0.1 + i * 0.08, ease: EASE }}
          className="flex items-center gap-2.5 rounded-lg border px-3 py-2"
          style={{ borderColor: sel ? "rgba(0,255,102,0.3)" : "rgba(255,255,255,0.05)", backgroundColor: sel ? "rgba(0,255,102,0.05)" : "rgba(255,255,255,0.02)" }}
        >
          <CheckSquare className="h-3 w-3 shrink-0" style={{ color: sel ? "#00FF66" : "rgba(255,255,255,0.2)" }} />
          <span className="h-1 flex-1 rounded-full bg-white/15" />
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: Object.values(STATUS_COLORS)[i] }} />
        </motion.div>
      ))}
    </div>
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay: 0.5, ease: EASE }}
      className="mt-4 flex flex-wrap items-center gap-1.5 rounded-2xl border border-white/10 bg-black/60 px-3 py-2.5 shadow-[0_16px_40px_rgba(0,0,0,0.5)]"
    >
      <span className="rounded-full bg-[#00FF66] px-2 py-0.5 text-[8px] font-black text-black">3 selected</span>
      {["Status", "Priority", "Owner", "Area", "Due"].map((f) => (
        <span key={f} className="rounded-full border border-white/10 px-2 py-0.5 text-[8px] font-bold text-white/55">{f}</span>
      ))}
      <span className="ml-auto flex items-center gap-1 text-[8px] font-bold text-[#FFB020]"><Undo2 className="h-2.5 w-2.5" /> Undo</span>
    </motion.div>
  </VisualCard>
);

/* ---------- announcements ---------- */
const AnnouncementViz = () => (
  <VisualCard>
    <div className="mb-4 flex items-center gap-3 rounded-xl border border-[#FF6B81]/30 bg-[#FF6B81]/[0.07] px-3.5 py-2">
      <span className="relative flex h-2 w-2 shrink-0">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#FF6B81] opacity-60" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-[#FF6B81]" />
      </span>
      <span className="truncate text-[10px] font-semibold text-white/75">10% package delivery · Oct 15</span>
      <span className="ml-auto shrink-0 text-[8px] font-bold uppercase tracking-widest text-[#FF6B81]">in 14 days</span>
    </div>
    <motion.div
      initial={{ opacity: 0, y: 18, scale: 0.95 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay: 0.15, ease: EASE }}
      className="overflow-hidden rounded-2xl border border-white/10 bg-[#0b0d0f]"
    >
      <div className="h-0.5 w-full bg-[#FF6B81] shadow-[0_0_14px_#FF6B81]" />
      <div className="p-4">
        <div className="flex items-center gap-2">
          <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-[#FF6B81]/15 text-[#FF6B81]"><Megaphone className="h-3 w-3" /></span>
          <span className="rounded-full bg-[#FF6B81] px-2 py-0.5 text-[8px] font-black uppercase tracking-widest text-black">Critical</span>
          <span className="text-[8px] font-semibold uppercase tracking-widest text-white/35">Whole workspace</span>
        </div>
        <p className="font-display mt-3 text-sm font-extrabold text-white">10% package delivery date confirmed</p>
        <div className="mt-3 flex items-center gap-2 rounded-lg border border-[#FF6B81]/25 bg-[#FF6B81]/[0.06] px-2.5 py-1.5">
          <CalendarClock className="h-3 w-3 text-[#FF6B81]" />
          <span className="text-[9px] font-bold text-white/80">Oct 15, 2026</span>
        </div>
      </div>
      <div className="flex items-center justify-between border-t border-white/5 px-4 py-2.5">
        <span className="flex items-center gap-1 text-[8px] font-bold text-white/40"><Eye className="h-2.5 w-2.5" /> 9 of 12 seen</span>
        <span className="rounded-full bg-[#FF6B81] px-2.5 py-1 text-[8px] font-black uppercase tracking-widest text-black">Acknowledge</span>
      </div>
    </motion.div>
  </VisualCard>
);


/* ---------- private to-dos with repeating reminders ---------- */
const TODOS = [
  { text: "Submit weekly timesheet", chip: "Fri 4:00 PM", repeat: "Every week" },
  { text: "Print P&IDs for client call", chip: "Today 2:00 PM" },
  { text: "Email vendor about gaskets", done: true },
];

const TodoViz = () => (
  <VisualCard>
    <div className="mb-4 flex items-center justify-between">
      <p className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.24em] text-[#00FF66]">
        <ListChecks className="h-3.5 w-3.5" /> Private to-dos
      </p>
      <span className="flex items-center gap-1 rounded-full border border-white/10 px-2.5 py-0.5 text-[9px] font-bold text-white/45"><Lock className="h-2.5 w-2.5" /> Only you</span>
    </div>
    <div className="space-y-1.5">
      {TODOS.map((t, i) => (
        <motion.div
          key={t.text}
          initial={{ opacity: 0, x: -12 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.45, delay: 0.1 + i * 0.1, ease: EASE }}
          className="flex items-start gap-2.5 rounded-xl border border-white/5 bg-white/[0.02] px-3 py-2"
        >
          <span className={`mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-[4px] border ${t.done ? "border-[#00FF66] bg-[#00FF66]" : "border-white/25"}`}>
            {t.done && <Check className="h-2.5 w-2.5 text-black" strokeWidth={3} />}
          </span>
          <div className="min-w-0 flex-1">
            <p className={`truncate text-[11px] font-semibold ${t.done ? "text-white/30 line-through" : "text-white/80"}`}>{t.text}</p>
            {t.chip && (
              <p className="mt-0.5 flex items-center gap-1.5 text-[8px] font-bold text-[#FFB020]/85">
                <Bell className="h-2.5 w-2.5" /> {t.chip}
                {t.repeat && <span className="flex items-center gap-1 text-[#00FF66]/80"><Repeat className="h-2.5 w-2.5" /> {t.repeat}</span>}
              </p>
            )}
          </div>
        </motion.div>
      ))}
    </div>
    <motion.div
      initial={{ opacity: 0, y: 16, scale: 0.96 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay: 0.5, ease: EASE }}
      className="ml-auto mt-4 w-[78%] overflow-hidden rounded-2xl border border-white/10 bg-[#0b0d0f] shadow-[0_20px_50px_rgba(0,0,0,0.6)]"
    >
      <div className="h-0.5 w-full bg-gradient-to-r from-[#FFB020] to-transparent" />
      <div className="p-3">
        <div className="flex items-center gap-2">
          <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-[#FFB020]/15 text-[#FFB020]"><BellRing className="h-3 w-3" /></span>
          <div className="min-w-0">
            <p className="text-[7px] font-bold uppercase tracking-[0.18em] text-[#FFB020]">Reminder</p>
            <p className="truncate text-[10px] font-bold text-white">Submit weekly timesheet</p>
          </div>
        </div>
        <span className="mt-2.5 flex items-center justify-center gap-1 rounded-lg bg-[#00FF66] py-1 text-[8px] font-black text-black"><Check className="h-2.5 w-2.5" /> Mark as done</span>
        <div className="mt-1.5 grid grid-cols-3 gap-1">
          {["10 min", "1 hour", "Tomorrow"].map((l) => (
            <span key={l} className="rounded-md border border-white/[0.08] bg-white/[0.03] py-0.5 text-center text-[7px] font-bold text-white/55">{l}</span>
          ))}
        </div>
      </div>
    </motion.div>
  </VisualCard>
);

/* ---------- live presence ---------- */
const PEOPLE = [
  { name: "User 1", role: "Project lead", state: "Online", color: "#00FF66" },
  { name: "User 2", role: "Reviewer", state: "Away · 12m", color: "#FFB020" },
  { name: "User 3", role: "Piping", state: "Online", color: "#00FF66" },
  { name: "User 4", role: "Electrical", state: "Last seen 3h ago", color: "rgba(255,255,255,0.25)" },
];

const PresenceViz = () => (
  <VisualCard>
    <div className="mb-5 flex items-center justify-between">
      <p className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.24em] text-white/40">
        <Users className="h-3.5 w-3.5" /> Team
      </p>
      <span className="flex items-center gap-1.5 rounded-full border border-[#00FF66]/40 bg-[#00FF66]/10 px-2.5 py-0.5 text-[9px] font-bold text-[#00FF66]">
        <span className="relative flex h-1.5 w-1.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#00FF66] opacity-70" />
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#00FF66]" />
        </span>
        2 online now
      </span>
    </div>
    <div className="space-y-2">
      {PEOPLE.map((p, i) => (
        <motion.div
          key={p.name}
          initial={{ opacity: 0, x: 16 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.5, delay: 0.12 + i * 0.1, ease: EASE }}
          className="flex items-center gap-3 rounded-2xl border border-white/5 bg-white/[0.03] px-3.5 py-2.5"
        >
          <span className="relative">
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white/10 text-[9px] font-bold text-white/70">U{i + 1}</span>
            <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-[#0A120D]" style={{ backgroundColor: p.color }} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-[11px] font-semibold text-white/80">{p.name}</p>
            <p className="text-[8px] font-bold uppercase tracking-widest text-white/35">{p.role}</p>
          </div>
          <span className="shrink-0 text-[9px] font-bold" style={{ color: p.color === "rgba(255,255,255,0.25)" ? "rgba(255,255,255,0.4)" : p.color }}>{p.state}</span>
        </motion.div>
      ))}
    </div>
  </VisualCard>
);

/* ---------- chapter block ---------- */
const Chapter = ({ num, title, body, visual, flip }) => (
  <div className="relative mt-28 grid grid-cols-1 items-center gap-10 md:grid-cols-12 md:gap-10">
    <motion.div {...reveal} className={`md:col-span-5 ${flip ? "md:order-2 md:col-start-8" : ""}`}>
      <div className="mb-5 flex items-baseline gap-4">
        <ChapterNumber num={num} />
        <h3 className="font-display text-3xl font-bold tracking-tight md:text-4xl" data-testid={`manifesto-chapter-${num}`}>
          {title}
        </h3>
      </div>
      <p className="max-w-lg text-base leading-relaxed text-white/55">{body}</p>
    </motion.div>
    <motion.div
      {...reveal}
      transition={{ ...reveal.transition, delay: 0.12 }}
      className={`md:col-span-6 ${flip ? "md:order-1 md:col-start-1" : "md:col-start-7"}`}
    >
      {visual}
    </motion.div>
  </div>
);

const CHAPTERS = [
  {
    title: "Six lanes. One glance.",
    body: (
      <>From <em>not started</em> to <em>review</em>, every task carries a status the whole
      company reads at a glance. The breakdown updates live as work moves — the donut never lies.</>
    ),
    visual: <StatusDonut />,
  },
  {
    title: "Drag, drop, done.",
    body: "The board is the heartbeat. Move a task across the six lanes and everything updates at once — status, progress, ownership. One move, one truth, everywhere.",
    visual: <BoardViz />,
  },
  {
    title: "One list, every company.",
    body: "My Work pulls every open task assigned to you across all your workspaces into a single list — overdue first, then due soon, then later. Belong to three client workspaces? See everything on your plate in one place, and jump straight into any task.",
    visual: <MyWorkViz />,
  },
  {
    title: "Nothing ships unreviewed.",
    body: "Send a task to review with a named reviewer and it lands in their To Review queue. Approve or request changes right from the list, swap reviewers without losing the clock, and every review cycle and day in review is counted — so quality gates never become bottlenecks.",
    visual: <ReviewViz />,
  },
  {
    title: "The whole schedule, in one line.",
    body: "A live timeline lays every task on a day-by-day gantt — start to due, filled by progress. Admins drag a bar to reschedule, and dependency arrows turn red the moment one task would start before another finishes.",
    visual: <TimelineViz />,
  },
  {
    title: "Progress you can feel.",
    body: "A live status donut and a weekly momentum curve show whether you're speeding up or stalling — real analytics, no status meeting required.",
    visual: <AnalyticsViz />,
  },
  {
    title: "Every owner, accounted for.",
    body: "Per-person load bars show who's drowning and who has room before standup even starts. Overload warnings turn capacity from a guess into a picture.",
    visual: <WorkloadBars />,
  },
  {
    title: "Disciplines, not chaos.",
    body: "Split a project into discipline groups — Mechanical, Piping, Electrical — each with its own lead and crew. Group leads run their own discipline's tasks and assignments, while project leads still see and steer the whole job.",
    visual: <GroupsViz />,
  },
  {
    title: "A hundred edits, one click.",
    body: "Select any set of tasks and change status, priority, owner, area, progress or due date in one move. Deleted something by mistake? Undo it instantly, or restore it from Recently deleted for up to seven days.",
    visual: <BulkViz />,
  },
  {
    title: "Time off, already handled.",
    body: "Request and approve time off in a couple of taps — with US public holidays baked into the calendar, so nobody plans a deadline around a closed office.",
    visual: <CalendarViz />,
  },
  {
    title: "Talk in context.",
    body: "Discuss work right on the task with @mentions, and a live notification bell keeps everyone in the loop on assignments, mentions and PTO decisions — in real time.",
    visual: <SignalStack />,
  },
  {
    title: "See who's here.",
    body: "Live presence shows who's online right now, who has stepped away and when everyone was last active — on the dashboard and the team page alike. Know whether a quick question will get a quick answer before you send it.",
    visual: <PresenceViz />,
  },
  {
    title: "Big news, impossible to miss.",
    body: "Post a major update — a delivery date, a milestone, a deadline change — and it pops up for everyone it concerns until they acknowledge it. Critical news stays pinned as a countdown banner until the key date, and you can see exactly who has seen it and remind the rest in one click.",
    visual: <AnnouncementViz />,
  },
  {
    title: "Think it, then do it.",
    body: "A private mind-map wall of colour-coded sticky notes for the reminders in your head. Branch related ideas with connector lines, then turn any note into a real task in one click when it's ready to ship.",
    visual: <MindmapViz />,
  },
  {
    title: "Never forget the small stuff.",
    body: "Private to-do checklists that only you can see, right beside your work. Set a reminder for any item, make it repeat — every day, every weekday, every week or every month — and stop it on a date or after a set number of times. Perfect for timesheets and routine checks. When it's due, a pop-up lets you tick it off or snooze it in one tap. Included on every plan.",
    visual: <TodoViz />,
  },
  {
    title: "Every action, on record.",
    body: "Every change is logged, searchable and exportable to CSV, with retention you control. When someone asks \u201cwho changed this?\u201d, you already know.",
    visual: <AuditViz />,
  },
  {
    title: "The right access, by role.",
    body: "Owners hold billing, admins run the workspace, project and group leads run their own work, reviewers sign it off — and Viewers give PMs and management read-only eyes on everything without touching a thing. Each project is fully isolated, and a Portfolio view gives admins every project's lead, headcount and overdue count at a glance. Enforced on the interface and the server.",
    visual: <RbacViz />,
  },
  {
    title: "Reports that read themselves.",
    body: "One click turns the whole picture into a clean, print-ready report — opening with an AI-written executive summary that reads the numbers for you, including how long work sits in review, followed by workload, tasks and time-off. Save it as a PDF and hand it straight to your project managers.",
    visual: <ReportViz />,
  },
].map((c, i) => ({ ...c, num: String(i + 1).padStart(2, "0") }));

export default function Manifesto() {
  return (
    <section id="manifesto" className="relative mx-auto max-w-7xl overflow-hidden px-6 py-32" data-testid="manifesto-section">
      <div className="pointer-events-none absolute inset-x-0 top-24 select-none overflow-hidden" aria-hidden="true">
        <div className="animate-marquee flex w-max opacity-[0.07]">
          {[0, 1].map((r) => (
            <span key={r} className="font-display flex shrink-0 items-center text-outline-green text-7xl font-black tracking-tight md:text-9xl">
              ONE SIGNAL&nbsp;•&nbsp;ZERO NOISE&nbsp;•&nbsp;ONE SIGNAL&nbsp;•&nbsp;ZERO NOISE&nbsp;•&nbsp;
            </span>
          ))}
        </div>
      </div>

      <div className="relative">
        <motion.p {...reveal} className="mb-6 text-xs font-bold uppercase tracking-[0.3em] text-[#00FF66]">
          The manifesto
        </motion.p>
        <motion.h2
          {...reveal}
          transition={{ ...reveal.transition, delay: 0.08 }}
          className="font-display max-w-4xl text-4xl font-black leading-[1.02] tracking-tighter md:text-6xl"
          data-testid="manifesto-headline"
        >
          Spreadsheets are dead.
          <br />
          Scattered tools are <span className="text-[#00FF66]">noise.</span>
        </motion.h2>
        <motion.p
          {...reveal}
          transition={{ ...reveal.transition, delay: 0.16 }}
          className="mt-8 max-w-2xl text-lg leading-relaxed text-white/55"
        >
          Your team doesn&apos;t need another tab to check. They need one signal. Vektor
          pulls tasks, capacity, time-off, collaboration and control into a single command
          center — so you always know who&apos;s doing what, what&apos;s at risk, and what ships next.
        </motion.p>
      </div>

      {CHAPTERS.map((c, i) => (
        <Chapter key={c.num} {...c} flip={i % 2 === 1} />
      ))}
    </section>
  );
}
